/**
 * AppLauncher — the home surface on phone & tablet (replaces the desktop
 * dashboard on those viewports). After sign-in the first thing the user sees is
 * the set of apps they're enabled for: a curated "day-of" row first, then a
 * "More" grid of every other area their role can open. Tapping an app routes to
 * the role/device-appropriate screen (e.g. a Coordinator tapping Event Day lands
 * on today's events they're responsible for).
 */
import { Icon, T } from "@v0y4ge/jrni-ui";
import type { ReactNode } from "react";
import { can, useUser, signOut } from "./access";
import { useNav } from "./router";
import { useViewport } from "./useResponsive";
import { useT } from "../lib/i18n";

const SANS = "var(--jrni-font-family-sans)";

interface AppDef { id: string; labelKey: string; desc: string; route: string; needs: string | string[]; icon: ReactNode; accent: string; }

/** Curated day-of apps — the things you reach for while running events. */
const DAY_OF: AppDef[] = [
  { id: "eventday", labelKey: "shell.appLauncher.eventDay", desc: "Run today's events on-site", route: "eventday", needs: ["onsite.walkup", "onsite.queue", "guests.checkin"], icon: <Icon.check />, accent: "#1F8A5B" },
  { id: "guests", labelKey: "shell.appLauncher.guests", desc: "Check-in & guest list", route: "guests", needs: "guests.read", icon: <Icon.users />, accent: "#0E7490" },
  { id: "messaging", labelKey: "shell.appLauncher.messaging", desc: "Message guests & flows", route: "messaging", needs: "campaigns.read", icon: <Icon.msg />, accent: "#5B8DEF" },
  { id: "reports", labelKey: "shell.appLauncher.reports", desc: "How your events perform", route: "reports", needs: "reports.read", icon: <Icon.chart />, accent: "#8955F2" },
];

/** Everything else the role can open, surfaced under "More". */
const MORE: AppDef[] = [
  { id: "events", labelKey: "shell.appLauncher.events", desc: "Browse & build events", route: "events", needs: "events.read", icon: <Icon.ticket />, accent: "#2D60EB" },
  { id: "calendar", labelKey: "shell.appLauncher.calendar", desc: "Week & month view", route: "calendar.week", needs: "events.read", icon: <Icon.calendar />, accent: "#0E7490" },
  { id: "campaigns", labelKey: "shell.appLauncher.campaigns", desc: "Email & SMS sends", route: "campaigns", needs: "campaigns.read", icon: <Icon.send />, accent: "#5B8DEF" },
  { id: "promos", labelKey: "shell.appLauncher.couponsPromos", desc: "Discounts & codes", route: "promos", needs: "promos.read", icon: <Icon.tags />, accent: "#C77F14" },
  { id: "approvals", labelKey: "shell.appLauncher.approvals", desc: "Review & sign off", route: "approvals", needs: ["settings.write", "campaigns.approve"], icon: <Icon.check />, accent: "#027D47" },
  { id: "locations", labelKey: "shell.appLauncher.locations", desc: "Venues & capacity", route: "locations", needs: "locations.read", icon: <Icon.loc />, accent: "#0E7490" },
  { id: "staff", labelKey: "shell.appLauncher.staff", desc: "People & roles", route: "staff", needs: "team.read", icon: <Icon.users />, accent: "#C13584" },
  { id: "reports2", labelKey: "shell.appLauncher.reports", desc: "Performance & money", route: "reports", needs: "reports.read", icon: <Icon.chart />, accent: "#8955F2" },
  { id: "settings", labelKey: "shell.appLauncher.settings", desc: "Workspace & team", route: "settings.general", needs: "settings.read", icon: <Icon.settings />, accent: "#697180" },
];

const hasAccess = (needs: string | string[]) => Array.isArray(needs) ? needs.some((n) => can(n)) : can(needs);

export default function AppLauncher() {
  const t = useT();
  const nav = useNav();
  const u = useUser();
  const vp = useViewport();
  const cols = vp === "tablet" ? 3 : 2;
  const firstName = (u.name || "there").split(" ")[0];

  const dayOf = DAY_OF.filter((a) => hasAccess(a.needs));
  const moreIds = new Set(dayOf.map((a) => a.route));
  const more = MORE.filter((a) => hasAccess(a.needs) && !moreIds.has(a.route));

  const Tile = ({ a, big }: { a: AppDef; big?: boolean }) => (
    <button onClick={() => nav.go(a.route)} style={{
      display: "flex", flexDirection: "column", gap: big ? 12 : 8, alignItems: "flex-start",
      padding: big ? 18 : 15, borderRadius: 16, border: `1px solid ${T.border}`, background: "#fff",
      cursor: "pointer", textAlign: "left", fontFamily: SANS, minHeight: big ? 132 : 104,
      transition: "transform 60ms, box-shadow 120ms, border-color 120ms",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = T.shadowMd; e.currentTarget.style.borderColor = a.accent; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = T.border; }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}>
      <span style={{ width: big ? 46 : 40, height: big ? 46 : 40, borderRadius: 12, background: `color-mix(in srgb, ${a.accent} 14%, #fff)`, color: a.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ display: "inline-flex", transform: big ? "scale(1.25)" : "scale(1.05)" }}>{a.icon}</span>
      </span>
      <span style={{ flex: 1 }}>
        <span style={{ display: "block", fontSize: big ? 16 : 14.5, fontWeight: 700, color: T.n1 }}>{t(a.labelKey)}</span>
        <span style={{ display: "block", fontSize: 12.5, color: T.soft, marginTop: 2, lineHeight: 1.4 }}>{a.desc}</span>
      </span>
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #F4F2FC 0%, #F7FaF9 220px, var(--jrni-color-surface-canvas) 320px)", fontFamily: SANS, overflowY: "auto" }}>
      <div style={{ maxWidth: 880, margin: "0 auto", padding: vp === "tablet" ? "28px 28px 40px" : "22px 18px 36px" }}>
        {/* Header */}
        <header style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
          <span style={{ width: 44, height: 44, borderRadius: 999, background: u.color || T.p1, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, flexShrink: 0 }}>{u.init || "?"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.n1, letterSpacing: -0.3, lineHeight: 1.2 }}>{t("shell.appLauncher.hi", "Hi,")}{" "}{firstName}</div>
            <div style={{ fontSize: 13, color: T.soft }}>{u.role}</div>
          </div>
          <button onClick={() => { signOut(); nav.reset("signin"); }} title={t("shell.appLauncher.signOut", "Sign out")} style={{ height: 36, padding: "0 14px", borderRadius: 999, border: `1px solid ${T.border}`, background: "#fff", color: T.n2, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: SANS }}>{t("shell.appLauncher.signOut", "Sign out")}</button>
        </header>

        {/* Day-of apps */}
        {dayOf.length ? <>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.soft, margin: "0 2px 10px" }}>{t("shell.appLauncher.yourApps", "Your apps")}</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, marginBottom: 28 }}>
            {dayOf.map((a) => <Tile key={a.id} a={a} big />)}
          </div>
        </> : null}

        {/* More */}
        {more.length ? <>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.soft, margin: "0 2px 10px" }}>{t("shell.appLauncher.more", "More")}</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
            {more.map((a) => <Tile key={a.id} a={a} />)}
          </div>
        </> : null}

        {!dayOf.length && !more.length ? (
          <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("shell.appLauncher.noAppsAreEnabledForYourRoleYet", "No apps are enabled for your role yet.")}</div>
        ) : null}
      </div>
    </div>
  );
}
